import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Layout, Menu } from 'antd';
import classNames from 'classnames';

import { appTitleSelector } from 'src/store/app';
import { fetchUserProfile } from 'src/store/profile';
import { REACT_APP_VERSION, SUPPORTED_LANGUAGES } from 'src/constants';
import i18n from 'src/i18n/i18n';

import { useLogout, useResize } from 'src/hooks';

import { useMenuItems } from './useMenuItems';
import { usePathnameTitle } from './usePathnameTitle';

// import Logo from 'src/assets/logo.webp';
import { Logo192 } from 'src/assets';
import './antLayout.scss';

const { Header, Sider, Content } = Layout;

export const AntLayout = (props) => {
  const { children } = props;

  const dispatch = useDispatch();

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const appTitle = useSelector(appTitleSelector);

  const { width } = useResize();
  const { MENU_ITEMS, LOGOUT_ITEMS } = useMenuItems();
  const { TITLE_BY_PATHNAME } = usePathnameTitle();
  const { onClickLogout } = useLogout();

  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    document.title = TITLE_BY_PATHNAME[pathname];
  }, [TITLE_BY_PATHNAME, pathname]);

  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  useEffect(() => {
    const storedLanguage = localStorage.getItem('language');

    if (SUPPORTED_LANGUAGES.includes(storedLanguage) && i18n.language !== storedLanguage) {
      i18n.changeLanguage(storedLanguage);
    }
  }, []);

  useEffect(() => {
    if (width < 768) {
      setIsCollapsed(true);
    }
  }, [width]);

  const onCollapseMenu = useCallback(() => {
    setIsCollapsed((prev) => !prev);
  }, []);

  const onClickSideItem = useCallback(
    (event) => {
      navigate(event.key);
    },
    [navigate]
  );

  const selectedMenuKeys = useMemo(() => {
    const exactKey = MENU_ITEMS.find((item) => item.key === pathname)?.key;

    if (exactKey) {
      return [exactKey];
    }

    const parentKey = MENU_ITEMS.find((item) => pathname.startsWith(item.key))?.key;

    return parentKey ? [parentKey] : [];
  }, [MENU_ITEMS, pathname]);

  const sliderStyle = classNames('ant-layout__sider', {
    'ant-layout__sider--hidden': isCollapsed
  });

  return (
    <Layout className="ant-layout">
      <Sider
        theme="light"
        collapsible
        collapsed={isCollapsed}
        onCollapse={onCollapseMenu}
        className={sliderStyle}
      >
        <div className="ant-layout__title">
          <img
            className="ant-layout__logo"
            src={Logo192}
            alt="Logo"
            style={{
              margin: isCollapsed ? 'auto' : 0
            }}
          />
          {!isCollapsed ? <h3>Bot</h3> : null}
        </div>
        <Menu
          theme="light"
          mode="inline"
          onClick={onClickSideItem}
          selectedKeys={selectedMenuKeys}
          items={MENU_ITEMS}
        />

        <Menu
          theme="light"
          mode="inline"
          className="ant-layout__logout"
          onClick={onClickLogout}
          selectable={false}
          items={LOGOUT_ITEMS}
        />
      </Sider>
      <Layout>
        <Header className="ant-layout__header">
          {appTitle}
          <div className="ant-layout__header__release">Release: v{REACT_APP_VERSION}</div>
        </Header>
        <Content className="ant-layout__content">{children ?? <Outlet />}</Content>
      </Layout>
    </Layout>
  );
};
